package meineglasfaser.api

import kotlinx.serialization.Serializable
import kotlin.time.Instant

enum class WorkflowKind {
    ORDER,
    SURVEY,
    SUPPORT;
}

enum class WorkflowStatus {
    IN_PROGRESS,
    COMPLETED,
    DISCARDED;
}

sealed interface WorkflowStepDescriptor {
    val key: String

    sealed interface Event : WorkflowStepDescriptor {
        data object Create : Event {
            override val key: String get() = "EVENT_CREATE"
        }
        data object UpdateStatus : Event {
            override val key: String get() = "EVENT_UPDATE_STATUS"
        }
        data object UpdateTitle : Event {
            override val key: String get() = "EVENT_UPDATE_TITLE"
        }
    }

    sealed interface LinkOrUnlink : WorkflowStepDescriptor

    sealed interface Link : LinkOrUnlink {
        data object Customer : Link {
            override val key: String get() = "LINK_CUSTOMER"
        }
        data object Order : Link {
            override val key: String get() = "LINK_ORDER"
        }
        data object SurveyEntry : Link {
            override val key: String get() = "LINK_SURVEY_ENTRY"
        }
    }

    sealed interface Unlink : LinkOrUnlink {
        data object Customer : Unlink {
            override val key: String get() = "UNLINK_CUSTOMER"
        }
        data object Order : Unlink {
            override val key: String get() = "UNLINK_ORDER"
        }
        data object SurveyEntry : Unlink {
            override val key: String get() = "UNLINK_SURVEY_ENTRY"
        }
    }

    sealed interface Note : WorkflowStepDescriptor {
        data object Freeform : Note {
            override val key: String get() = "NOTE_FREEFORM"
        }
    }

    companion object {
        fun valueOf(key: String): WorkflowStepDescriptor {
            return when (key) {
                Event.Create.key -> Event.Create
                Event.UpdateStatus.key -> Event.UpdateStatus
                Event.UpdateTitle.key -> Event.UpdateTitle
                Link.Customer.key -> Link.Customer
                Link.Order.key -> Link.Order
                Link.SurveyEntry.key -> Link.SurveyEntry
                Unlink.Customer.key -> Unlink.Customer
                Unlink.Order.key -> Unlink.Order
                Unlink.SurveyEntry.key -> Unlink.SurveyEntry
                Note.Freeform.key -> Note.Freeform
                else -> error("Invalid descriptor key: $key")
            }
        }
    }
}

enum class WorkflowStepColor(val light: String, val dark: String) {
    DEFAULT(light = "#ffffff", dark = "#000000"),
    RED(light = "#ffcccc", dark = "#660000"),
    ORANGE(light = "#ffe5b4", dark = "#7a4b00"),
    YELLOW(light = "#ffffcc", dark = "#7f7f00"),
    GREEN(light = "#d4edda", dark = "#004d00"),
    TURQUOISE(light = "#b2e0e0", dark = "#004d4d"),
    BLUE(light = "#cce5ff", dark = "#003366"),
    PURPLE(light = "#e6ccff", dark = "#4b0082"),
    GRAY(light = "#f0f0f0", dark = "#4d4d4d");
}

@Serializable
data class Workflow(
    override val id: String,
    override val createdAt: Instant,
    override val createdBy: String,
    override val updatedAt: Instant,
    override val updatedBy: String,
    val nid: Int,
    val kind: WorkflowKind,
    val status: WorkflowStatus,
    val title: String,
    val linkedCustomerId: String?,
    val linkedOrderId: String?,
    val linkedSurveyEntryId: String?,
    val steps: List<WorkflowStep>,
) : Entity

@Serializable
data class WorkflowStep(
    override val id: String,
    override val createdAt: Instant,
    override val createdBy: String,
    override val updatedAt: Instant,
    override val updatedBy: String,
    val descriptor: WorkflowStepDescriptor,
    val newWfStatus: WorkflowStatus?,
    val newWfTitle: String?,
    val linkId: String?,
    val text: String?,
    val color: WorkflowStepColor?,
) : Entity

@Serializable
data class WorkflowCreateRequest(
    val kind: WorkflowKind,
    val title: String,
    val linkedCustomerId: String?,
    val linkedOrderId: String?,
    val linkedSurveyEntryId: String?,
)

@Serializable
data class WorkflowCreateResponse(
    val nid: Int,
)

@Serializable
data class WorkflowCreateFreeformNoteRequest(
    val workflowId: String,
    val text: String,
    val color: WorkflowStepColor,
)

@Serializable
data class WorkflowUpdateFreeformNoteRequest(
    val id: String,
    val workflowId: String,
    val text: String,
    val color: WorkflowStepColor,
)
